import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { ReportCard } from '../../components/reports/ReportCard';
import { ReportTemplates } from '../../components/reports/ReportTemplates';
import { ScheduledReports } from '../../components/reports/ScheduledReports';
import { CreateReportModal } from '../../components/reports/CreateReportModal';
import { TemplatePreviewModal } from '../../components/reports/TemplatePreviewModal';
import { EditReportModal } from '../../components/reports/EditReportModal';
import { ProjectSelector } from '../../components/projects/ProjectSelector';
import { useReports } from '../../hooks/useReports';
import { useProjects } from '../../hooks/useProjects';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { reportsApi } from '../../services/api';
import { 
  Plus, 
  Search, 
  Filter, 
  FileText, 
  Calendar, 
  Download,
  Sparkles,
  Loader,
  FolderOpen,
  BarChart3
} from 'lucide-react';

export function ReportsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<any>(null);
  
  const { loading: authLoading } = useAuth();
  const { projects, isLoading: projectsLoading } = useProjects();
  const { useAllReports, useScheduledReports, generateReport, deleteReport, isDeleting } = useReports();
  
  // Set default project when projects load
  useEffect(() => {
    if (projects && projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);
  
  // Get all reports
  const { data: reportsData, isLoading: reportsLoading, error: reportsError, refetch: refetchReports } = useAllReports();
  const reports = reportsData?.reports || [];
  
  // Get scheduled reports
  const { data: scheduledData, isLoading: scheduledLoading } = useScheduledReports();
  const scheduledReports = scheduledData?.reports || [];

  const filteredReports = reports.filter((report: any) =>
    report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleGenerateReport = async (report: any) => {
    try {
      await generateReport(report.id);
    } catch (error) {
      console.error('Failed to generate report:', error);
    }
  };

  const handleExportReport = async (report: any, format: string = 'pdf') => {
    try {
      const blob = await reportsApi.exportReport(report.id, format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const handleExportAllReports = async (format: string = 'pdf') => {
    try {
      if (filteredReports.length === 0) {
        alert('No reports to export');
        return;
      }

      // Export each report individually
      for (const report of filteredReports) {
        await handleExportReport(report, format);
        // Add a small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error('Failed to export all reports:', error);
      alert('Failed to export all reports. Please try again.');
    }
  };

  const handleEditReport = (report: any) => {
    setSelectedReport(report);
    setShowEditModal(true);
  };

  const handleDeleteReport = (report: any) => {
    setReportToDelete(report);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      await deleteReport(reportToDelete.id);
      setShowDeleteConfirm(false);
      setReportToDelete(null);
      refetchReports(); // Refresh the reports list
    } catch (error) {
      console.error('Failed to delete report:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setReportToDelete(null);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedReport(null);
  };

  const handleReportUpdated = () => {
    refetchReports();
  };

  const handleTemplateSelect = (template: any) => {
    console.log('Selected template:', template);
    setSelectedTemplate(template);
    setShowTemplatePreview(true);
  };

  const handleCreateFromTemplate = async (template: any, customization: any) => {
    try {
      // This will be handled by the TemplatePreviewModal itself
      console.log('Creating report from template:', template, customization);
    } catch (error) {
      console.error('Failed to create report from template:', error);
    }
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  if (authLoading || reportsLoading || projectsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Create, schedule, and manage your marketing reports
            </p>
          </div>
        </div>
        <Card className="p-12 text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600 dark:text-blue-400" />
          <p className="text-gray-600 dark:text-gray-300">Loading reports...</p>
        </Card>
      </div>
    );
  }

  if (reportsError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Create, schedule, and manage your marketing reports
            </p>
          </div>
        </div>
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Reports</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            There was an error loading your reports. Please try again.
          </p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Create, schedule, and manage your marketing reports
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="relative group">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="p-2">
                <button
                  onClick={() => handleExportAllReports('pdf')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export as PDF
                </button>
                <button
                  onClick={() => handleExportAllReports('csv')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExportAllReports('xlsx')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export as Excel
                </button>
              </div>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Report
          </Button>
        </div>
      </div>

      {/* Project Selector */}
      {projects && projects.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <FolderOpen className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Project:</span>
              </div>
              <ProjectSelector
                projects={projects}
                selectedProjectId={selectedProjectId}
                onProjectSelect={handleProjectSelect}
                isLoading={projectsLoading}
              />
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {selectedProjectId ? 'Templates and widgets will be filtered by available data sources' : 'Select a project to see available templates'}
            </div>
          </div>
        </Card>
      )}

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>
      </Card>

      {/* Reports Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all" className="flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            All Reports
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Scheduled
          </TabsTrigger>
          <TabsTrigger value="ai-generated" className="flex items-center">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Generated
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6">
          {filteredReports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredReports.map((report: any) => (
                <ReportCard 
                  key={report.id} 
                  report={report}
                  onGenerate={handleGenerateReport}
                  onDownload={handleExportReport}
                  onEdit={handleEditReport}
                  onDelete={handleDeleteReport}
                />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {searchQuery ? 'No reports found' : 'No reports yet'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
                {searchQuery 
                  ? `No reports match "${searchQuery}". Try adjusting your search.`
                  : 'Create your first report to start generating insights from your data.'
                }
              </p>
              {!searchQuery && (
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Report
                </Button>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          <ReportTemplates 
            onSelectTemplate={handleTemplateSelect} 
            projectId={selectedProjectId}
          />
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-6">
          <ScheduledReports 
            reports={scheduledReports}
            isLoading={scheduledLoading}
          />
        </TabsContent>

        <TabsContent value="ai-generated" className="space-y-6">
          <Card className="p-12 text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-100 dark:from-blue-900/30 to-purple-100 dark:to-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">AI-Generated Reports</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
              Let our AI agents automatically generate reports based on your data patterns and business goals.
            </p>
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <Sparkles className="w-4 h-4 mr-2" />
              Generate AI Report
            </Button>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Stats */}
      {filteredReports.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Reports</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{reports.length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
                <Calendar className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Scheduled</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {scheduledReports.length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-3">
                <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Templates</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {reports.filter((r: any) => r.report_type === 'template').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mr-3">
                <Sparkles className="w-4 h-4 text-pink-600 dark:text-pink-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">AI Generated</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {reports.filter((r: any) => r.report_type === 'ai_generated').length}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Create Report Modal */}
      <CreateReportModal 
        isOpen={showCreateModal} 
        onClose={() => setShowCreateModal(false)} 
      />

      {/* Template Preview Modal */}
      <TemplatePreviewModal
        isOpen={showTemplatePreview}
        template={selectedTemplate}
        onClose={() => {
          setShowTemplatePreview(false);
          setSelectedTemplate(null);
        }}
        onCreateFromTemplate={handleCreateFromTemplate}
      />

      {/* Edit Report Modal */}
      <EditReportModal
        isOpen={showEditModal}
        report={selectedReport}
        onClose={handleEditModalClose}
        onUpdated={handleReportUpdated}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
                <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Report</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>"{reportToDelete?.name}"</strong>? 
              This will permanently remove the report and all its generated data.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button 
                variant="outline" 
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Report'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}